import type { EdgeInput, NodeInput } from '@rematter/frontend-lib-api-client';
import { Node, Edge } from '@xyflow/react';

import { TriggerType } from '../types';

import { automationFullFragment } from './fragments';

import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

function sanitizeConfig(
  config: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config)) {
    if (!key.startsWith('_')) {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

export type AutomationCreateParams = {
  name: string;
  description?: string | null;
  nodes: Node[];
  edges: Edge[];
  triggerType: TriggerType;
  triggerConfig: Record<string, unknown>;
};

/**
 * Converts a frontend AutomationNode to GraphQL NodeInput format.
 * The frontend uses Node<data: { label, description, config }> while
 * GraphQL uses a flat NodeInput with all fields at the top level.
 */
function convertAutomationNodeToGraphQLInput(
  node: Node,
): Record<string, unknown> {
  const { id, type, position, data } = node;

  // Start with base fields
  const input: Record<string, unknown> = {
    id,
    type,
    position,
    label: data.label,
    description: data.description,
  };

  // Handle type-specific config transformations
  if (type === 'BusinessEventTrigger' && data.config) {
    const config = data.config as {
      source?: { case?: string; value?: { event?: { case?: string } } };
      filterExpression?: string;
    };

    // Transform source to eventType: "entityType.eventCase"
    if (config.source?.case) {
      const entityType = config.source.case;
      const eventCase = config.source.value?.event?.case;
      input.eventType = eventCase ? `${entityType}.${eventCase}` : entityType;
    }

    // Transform filterExpression to filter if needed
    if (config.filterExpression) {
      input.filter = { expression: config.filterExpression };
    }
  } else if (type === 'Loop' && data.config) {
    const config = data.config as {
      arrayExpression?: string;
      itemVariableName?: string;
      maxIterations?: number;
    };
    input.loopArrayExpression = config.arrayExpression ?? '';
    input.loopItemVariableName = config.itemVariableName ?? 'item';
    input.loopMaxIterations = config.maxIterations ?? 100;
  } else if (data.config && typeof data.config === 'object') {
    Object.assign(
      input,
      sanitizeConfig(data.config as Record<string, unknown>),
    );
  }

  return input;
}

/**
 * Converts frontend triggerConfig to GraphQL TriggerConfigInput format.
 * The frontend uses source.case/source.value.event.case while GraphQL expects entityType/eventCase.
 */
function convertTriggerConfigToGraphQLInput(
  triggerType: TriggerType,
  triggerConfig: Record<string, unknown>,
): Record<string, unknown> {
  if (triggerType === 'BusinessEvent') {
    const config = triggerConfig as {
      source?: { case?: string; value?: { event?: { case?: string } } };
      filterExpression?: string;
    };

    return {
      entityType: config.source?.case,
      eventCase: config.source?.value?.event?.case,
      filterExpression: config.filterExpression,
    };
  }

  if (triggerType === 'Schedule') {
    const config = triggerConfig as {
      cronExpression?: string;
      timezone?: string;
    };
    return {
      cron: config.cronExpression,
    };
  }

  return {};
}

/**
 * Converts a React Flow Edge to GraphQL EdgeInput format.
 */
function convertReactFlowEdgeToGraphQLInput(edge: Edge): {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
} {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle ?? null,
  };
}

export const automationCreateAtom = createMutationAtomFactory(
  'automations.create',
  (params: AutomationCreateParams) => ({
    automationsAutomationCreate: {
      __args: {
        input: {
          name: params.name,
          description: params.description ?? null,
          nodes: params.nodes.map(
            convertAutomationNodeToGraphQLInput,
          ) as unknown as NodeInput[],
          edges: (() => {
            const nodeIds = new Set(params.nodes.map((n) => n.id));
            return params.edges
              .filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
              .map(convertReactFlowEdgeToGraphQLInput);
          })() as unknown as EdgeInput[],
          triggerType: params.triggerType,
          triggerConfig: convertTriggerConfigToGraphQLInput(
            params.triggerType,
            params.triggerConfig,
          ),
        },
      },
      ...automationFullFragment,
    },
  }),
);
